/**
 * Per-task drilldown drawer for the Task List Manager.
 *
 * Tabs:
 *   • Details — taskText, riskBeingMitigated, condition policy + status,
 *     compensating factors, authority/clearing.
 *   • Applicability — toggle product + review-mode flags.
 *   • Conditions — embedded LOS-shaped conditions emitted on task failure.
 *     Reuses the same editor as the loan_processing document drawer so the
 *     LOS can consume task- and doc-emitted conditions indistinguishably.
 *   • Related — pick related tasks (from the in-memory list) + related
 *     LOS document types (free-form pill list since we don't fetch the
 *     LOS doc list here; user can paste docTypes).
 *   • Audit — newest-first per-field change log.
 *
 * Save is buffered: a single PATCH per Save button click so the audit
 * trail gets one entry per save rather than one-per-field.
 */
import { Component, useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, Button, IconButton, CircularProgress } from "@mui/material";
import CloseRounded from "@mui/icons-material/CloseRounded";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import PlaylistAddCheckOutlined from "@mui/icons-material/PlaylistAddCheckOutlined";
import LinkOutlined from "@mui/icons-material/LinkOutlined";
import HistoryOutlined from "@mui/icons-material/HistoryOutlined";
import SaveOutlined from "@mui/icons-material/SaveOutlined";
import SearchOutlined from "@mui/icons-material/SearchOutlined";
import AddRounded from "@mui/icons-material/AddRounded";
import GppMaybeOutlined from "@mui/icons-material/GppMaybeOutlined";
import { useTaskListsHook } from "../../hooks/useTaskListsHook";
import { ConditionsEditor } from "./ConditionsEditor";
import InputWrapper from "../../components/inputs/shared/InputWrapper";
import TextInput from "../../components/inputs/TextInput";
import SelectInput from "../../components/inputs/SelectInput";
import { formatDateTime } from "../../_src_shared/utils/functions/formatDateTime";

class DrawerErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[Task drawer crash]", error, info.componentStack);
  }
  render() {
    if (this.state.error) {
      return (
        <Box
          sx={{
            m: "16px",
            p: "12px",
            bgcolor: "#fff1f2",
            border: "1px solid #fecdd3",
            borderRadius: "8px",
            fontSize: 12,
            color: "#9f1239",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <Box component="p" sx={{ fontWeight: 600, color: "#881337", m: 0 }}>
            Render error
          </Box>
          <Box component="p" sx={{ fontFamily: "monospace", wordBreak: "break-all", m: 0 }}>
            {String(this.state.error?.message || this.state.error)}
          </Box>
          <Box
            component="button"
            onClick={() => this.setState({ error: null })}
            sx={{
              px: "8px",
              py: "4px",
              bgcolor: "#ffe4e6",
              borderRadius: "4px",
              fontSize: 11,
              border: "none",
              cursor: "pointer",
              alignSelf: "flex-start",
              "&:hover": { bgcolor: "#ffe4e6" },
            }}
          >
            Try again
          </Box>
        </Box>
      );
    }
    return this.props.children;
  }
}

export default function TaskDrilldownDrawer({ task, allTasks, onClose, onSave }) {
  const { getTaskAudit } = useTaskListsHook();

  const [tab, setTab] = useState("details");

  // Local buffers
  const [taskText, setTaskText] = useState("");
  const [riskBeingMitigated, setRiskBeingMitigated] = useState("");
  const [loanLevelConditionPolicy, setLoanLevelConditionPolicy] = useState("");
  const [reviewer, setReviewer] = useState("");
  const [authorityToWaive, setAuthorityToWaive] = useState("");
  const [clearingParty, setClearingParty] = useState("");
  const [compensatingFactors, setCompensatingFactors] = useState([]);
  const [factorDraft, setFactorDraft] = useState("");
  const [taskStatus, setTaskStatus] = useState("Fail");
  const [productApplicability, setProductApplicability] = useState({
    DSCR: false,
    RTL: false,
    GUC: false,
    MF: false,
  });
  const [reviewApplicability, setReviewApplicability] = useState({
    pre_close_full: false,
    pre_close_light: false,
    post_close_full: false,
    post_close_light: false,
    post_close_super_light: false,
    closing: false,
  });
  const [relatedTaskIds, setRelatedTaskIds] = useState([]);
  const [relatedDocuments, setRelatedDocuments] = useState([]);
  const [documentConditions, setDocumentConditions] = useState([]);

  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const [audit, setAudit] = useState([]);
  const [auditLoaded, setAuditLoaded] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);

  const lastTaskId = useRef(null);

  useEffect(() => {
    if (!task) {
      lastTaskId.current = null;
      return;
    }
    if (task.id !== lastTaskId.current) {
      setTaskText(task.taskText || "");
      setRiskBeingMitigated(task.riskBeingMitigated || "");
      setLoanLevelConditionPolicy(task.loanLevelConditionPolicy || "");
      setReviewer(task.reviewer || "");
      setAuthorityToWaive(task.authorityToWaive || "");
      setClearingParty(task.clearingParty || "");
      setCompensatingFactors(
        Array.isArray(task.compensatingFactors)
          ? [...task.compensatingFactors]
          : task.compensatingFactors
            ? [String(task.compensatingFactors)]
            : [],
      );
      setFactorDraft("");
      setTaskStatus(task.taskStatus || "Fail");
      setProductApplicability({
        ...(task.productApplicability || { DSCR: false, RTL: false, GUC: false, MF: false }),
      });
      const defaults = {
        pre_close_full: false,
        pre_close_light: false,
        post_close_full: false,
        post_close_light: false,
        post_close_super_light: false,
        closing: false,
      };
      setReviewApplicability({ ...defaults, ...(task.reviewApplicability || {}) });
      setRelatedTaskIds([...(task.relatedTaskIds || [])]);
      setRelatedDocuments([...(task.relatedDocuments || [])]);
      setDocumentConditions(
        Array.isArray(task.documentConditions)
          ? task.documentConditions.map((c) => ({ ...c }))
          : [],
      );
      setDirty(false);
      setAuditLoaded(false);
      setAudit([]);
      setTab("details");
      lastTaskId.current = task.id;
    }
  }, [task]);

  useEffect(() => {
    if (tab !== "audit" || !task || auditLoaded) return;
    setAuditLoading(true);
    getTaskAudit(task.id)
      .then(setAudit)
      .catch(() => setAudit([]))
      .finally(() => {
        setAuditLoaded(true);
        setAuditLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, task, auditLoaded]);

  if (!task) return null;

  const markDirty = () => setDirty(true);

  const handleSave = async () => {
    if (!task) return;
    const patch = {};
    if (taskText !== (task.taskText || "")) patch.taskText = taskText;
    if (riskBeingMitigated !== (task.riskBeingMitigated || "")) patch.riskBeingMitigated = riskBeingMitigated;
    if (loanLevelConditionPolicy !== (task.loanLevelConditionPolicy || "")) patch.loanLevelConditionPolicy = loanLevelConditionPolicy;
    if (JSON.stringify(compensatingFactors) !== JSON.stringify(task.compensatingFactors || [])) {
      patch.compensatingFactors = compensatingFactors;
    }
    if (reviewer !== (task.reviewer || "")) patch.reviewer = reviewer;
    if (authorityToWaive !== (task.authorityToWaive || "")) patch.authorityToWaive = authorityToWaive;
    if (clearingParty !== (task.clearingParty || "")) patch.clearingParty = clearingParty;
    if (taskStatus !== (task.taskStatus || "Fail")) patch.taskStatus = taskStatus;
    if (JSON.stringify(productApplicability) !== JSON.stringify(task.productApplicability)) {
      patch.productApplicability = productApplicability;
    }
    if (JSON.stringify(reviewApplicability) !== JSON.stringify(task.reviewApplicability)) {
      patch.reviewApplicability = reviewApplicability;
    }
    if (JSON.stringify(relatedTaskIds) !== JSON.stringify(task.relatedTaskIds || [])) {
      patch.relatedTaskIds = relatedTaskIds;
    }
    if (JSON.stringify(relatedDocuments) !== JSON.stringify(task.relatedDocuments || [])) {
      patch.relatedDocuments = relatedDocuments;
    }
    if (JSON.stringify(documentConditions) !== JSON.stringify(task.documentConditions || [])) {
      patch.documentConditions = documentConditions;
    }
    if (Object.keys(patch).length === 0) {
      setDirty(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(patch);
      setDirty(false);
      setAuditLoaded(false);
    } catch (err) {
      alert(err?.response?.data?.detail || err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Box
        onClick={onClose}
        aria-hidden
        sx={{ position: "fixed", inset: 0, zIndex: 1300, bgcolor: "rgba(0,0,0,0.3)" }}
      />
      <Box
        component="aside"
        sx={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          zIndex: 1301,
          width: 640,
          maxWidth: "95vw",
          bgcolor: "#fff",
          borderLeft: "1px solid #d1d5db",
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "12px",
            px: "20px",
            py: "12px",
            borderBottom: "1px solid #d1d5db",
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Box
              component="p"
              sx={{
                m: 0,
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: "#64748b",
              }}
            >
              {task.sourceDocument || "—"}
              {task.orderOfReview ? ` · #${task.orderOfReview}` : ""}
            </Box>
            <Typography
              component="h2"
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: "#0f172a",
                m: 0,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {taskText || task.taskText}
            </Typography>
            {task.last_edited_by && (
              <Box component="p" sx={{ m: 0, mt: "4px", fontSize: 11, color: "#64748b" }}>
                Last edited by {task.last_edited_by}
                {task.last_edited_at ? ` · ${formatDateTime(task.last_edited_at, "datetime")}` : ""}
              </Box>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
            <Button
              onClick={handleSave}
              disabled={!dirty || saving}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                px: "12px",
                py: "6px",
                fontSize: 12,
                textTransform: "none",
                bgcolor: "#2563eb",
                color: "#fff",
                borderRadius: "4px",
                minWidth: 0,
                "&:hover": { bgcolor: "#1d4ed8" },
                "&.Mui-disabled": { opacity: 0.4, color: "#fff", bgcolor: "#2563eb" },
              }}
            >
              {saving ? (
                <CircularProgress size={12} sx={{ color: "#fff" }} />
              ) : (
                <SaveOutlined sx={{ fontSize: 12 }} />
              )}
              Save
            </Button>
            <IconButton
              onClick={onClose}
              aria-label="Close drawer"
              sx={{
                p: "6px",
                color: "#64748b",
                borderRadius: "4px",
                "&:hover": { color: "#0f172a", bgcolor: "#f3f4f6" },
              }}
            >
              <CloseRounded sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "4px",
            px: "12px",
            py: "8px",
            borderBottom: "1px solid #d1d5db",
            bgcolor: "#f9fafb",
          }}
        >
          <TabButton id="details" icon={<DescriptionOutlined sx={{ fontSize: 12 }} />} label="Details" tab={tab} setTab={setTab} />
          <TabButton id="applicability" icon={<PlaylistAddCheckOutlined sx={{ fontSize: 12 }} />} label="Applicability" tab={tab} setTab={setTab} />
          <TabButton id="conditions" icon={<GppMaybeOutlined sx={{ fontSize: 12 }} />} label={`Conditions (${documentConditions.length})`} tab={tab} setTab={setTab} />
          <TabButton id="related" icon={<LinkOutlined sx={{ fontSize: 12 }} />} label={`Related (${relatedTaskIds.length + relatedDocuments.length})`} tab={tab} setTab={setTab} />
          <TabButton id="audit" icon={<HistoryOutlined sx={{ fontSize: 12 }} />} label="Audit" tab={tab} setTab={setTab} />
        </Box>

        <Box sx={{ flex: 1, overflow: "auto", p: "20px", display: "flex", flexDirection: "column", gap: "20px" }}>
          <DrawerErrorBoundary>
            {tab === "details" && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                <RffField label="Task text" helperText="Multi-line confirmation statement.">
                  <TextInput
                    value={taskText}
                    onChange={(v) => {
                      setTaskText(v);
                      markDirty();
                    }}
                    placeholder="Confirm that …"
                    multiline
                    minRows={4}
                    size="small"
                    inputProps={{ spellCheck: true }}
                  />
                </RffField>

                <RffField
                  label="Risk being mitigated"
                  required
                  helperText="Required for every task — describes what risk this checklist item is preventing."
                >
                  <TextInput
                    value={riskBeingMitigated}
                    onChange={(v) => {
                      setRiskBeingMitigated(v);
                      markDirty();
                    }}
                    placeholder="e.g. Prevents lender from funding a loan against an unverified or fraudulent collateral position."
                    multiline
                    minRows={3}
                    size="small"
                  />
                </RffField>

                <RffField
                  label="Loan-level condition policy"
                  helperText="The condition that fires on failure."
                >
                  <TextInput
                    value={loanLevelConditionPolicy}
                    onChange={(v) => {
                      setLoanLevelConditionPolicy(v);
                      markDirty();
                    }}
                    placeholder="e.g. Final HUD: Buyer/Seller information mismatch"
                    size="small"
                  />
                </RffField>

                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                  <RffField label="Reviewer">
                    <TextInput
                      value={reviewer}
                      onChange={(v) => {
                        setReviewer(v);
                        markDirty();
                      }}
                      placeholder="Underwriting"
                      size="small"
                    />
                  </RffField>
                  <RffField label="Status">
                    <SelectInput
                      value={taskStatus}
                      onChange={(v) => {
                        setTaskStatus(v);
                        markDirty();
                      }}
                      options={["Fail", "Pass", "Warn"]}
                    />
                  </RffField>
                  <RffField label="Authority to waive">
                    <TextInput
                      value={authorityToWaive}
                      onChange={(v) => {
                        setAuthorityToWaive(v);
                        markDirty();
                      }}
                      placeholder="Management"
                      size="small"
                      inputProps={{ list: "authority-hints" }}
                    />
                    <Box component="datalist" id="authority-hints">
                      {["Underwriter 1", "Head of Underwriting", "Management", "Head of Legal", "Not Waivable"].map((v) => (
                        <option key={v} value={v} />
                      ))}
                    </Box>
                  </RffField>
                  <RffField label="Clearing party">
                    <SelectInput
                      value={clearingParty}
                      onChange={(v) => {
                        setClearingParty(v);
                        markDirty();
                      }}
                      placeholder="—"
                      options={["Internal", "External"]}
                    />
                  </RffField>
                </Box>

                <RffField
                  label={`Compensating factors (${compensatingFactors.length})`}
                  helperText="ALL listed factors must be met for the failure to be cleared without a formal waiver. Add one per pill."
                >
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mb: "8px" }}>
                    {compensatingFactors.map((f, i) => (
                      <Box
                        component="span"
                        key={`${f}-${i}`}
                        sx={{
                          display: "inline-flex",
                          alignItems: "flex-start",
                          gap: "4px",
                          px: "8px",
                          py: "4px",
                          borderRadius: "6px",
                          fontSize: 12,
                          bgcolor: "#ecfdf5",
                          color: "#065f46",
                          border: "1px solid #a7f3d0",
                          maxWidth: "100%",
                        }}
                      >
                        <Box component="span" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          {f}
                        </Box>
                        <Box
                          component="button"
                          onClick={() => {
                            setCompensatingFactors(compensatingFactors.filter((_, j) => j !== i));
                            markDirty();
                          }}
                          aria-label={`Remove factor ${f}`}
                          sx={{
                            color: "#059669",
                            flexShrink: 0,
                            border: "none",
                            background: "none",
                            p: 0,
                            cursor: "pointer",
                            display: "inline-flex",
                            "&:hover": { color: "#0f172a" },
                          }}
                        >
                          <CloseRounded sx={{ fontSize: 11 }} />
                        </Box>
                      </Box>
                    ))}
                    {compensatingFactors.length === 0 && (
                      <Box component="span" sx={{ fontSize: 12, color: "#64748b", fontStyle: "italic" }}>
                        No compensating factors yet — failures require a formal waiver.
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                    <Box sx={{ flex: 1 }}>
                      <TextInput
                        value={factorDraft}
                        onChange={(v) => setFactorDraft(v)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            const v = factorDraft.trim();
                            if (!v || compensatingFactors.includes(v)) return;
                            setCompensatingFactors([...compensatingFactors, v]);
                            setFactorDraft("");
                            markDirty();
                          }
                        }}
                        placeholder="Add a compensating factor (Enter)"
                        size="small"
                      />
                    </Box>
                    <Button
                      onClick={() => {
                        const v = factorDraft.trim();
                        if (!v || compensatingFactors.includes(v)) return;
                        setCompensatingFactors([...compensatingFactors, v]);
                        setFactorDraft("");
                        markDirty();
                      }}
                      sx={{
                        minWidth: 0,
                        px: "12px",
                        py: "7px",
                        bgcolor: "#ecfdf5",
                        color: "#065f46",
                        border: "1px solid #a7f3d0",
                        "&:hover": { bgcolor: "#a7f3d0" },
                      }}
                    >
                      <AddRounded sx={{ fontSize: 14 }} />
                    </Button>
                  </Box>
                </RffField>
              </Box>
            )}

            {tab === "applicability" && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                <Section label="Loan products" hint="Toggle which products this task applies to.">
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                    {["DSCR", "RTL", "GUC", "MF"].map((k) => (
                      <Box
                        component="label"
                        key={k}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          px: "10px",
                          py: "6px",
                          bgcolor: "#fff",
                          border: "1px solid #d1d5db",
                          borderRadius: "4px",
                          fontSize: 14,
                          color: "#334155",
                          cursor: "pointer",
                        }}
                      >
                        <Box
                          component="input"
                          type="checkbox"
                          checked={productApplicability[k]}
                          onChange={(e) => {
                            setProductApplicability({ ...productApplicability, [k]: e.target.checked });
                            markDirty();
                          }}
                          sx={{ accentColor: "#3b82f6" }}
                        />
                        {k}
                      </Box>
                    ))}
                  </Box>
                </Section>

                <Section label="Review modes" hint="Closing is its own mode; the others compose freely.">
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                    {Object.keys(reviewApplicability).map((k) => (
                      <Box
                        component="label"
                        key={k}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          px: "10px",
                          py: "6px",
                          bgcolor: "#fff",
                          border: "1px solid #d1d5db",
                          borderRadius: "4px",
                          fontSize: 14,
                          color: "#334155",
                          cursor: "pointer",
                        }}
                      >
                        <Box
                          component="input"
                          type="checkbox"
                          checked={reviewApplicability[k]}
                          onChange={(e) => {
                            setReviewApplicability({ ...reviewApplicability, [k]: e.target.checked });
                            markDirty();
                          }}
                          sx={{ accentColor: "#3b82f6" }}
                        />
                        {k}
                      </Box>
                    ))}
                  </Box>
                </Section>
              </Box>
            )}

            {tab === "conditions" && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <Box component="p" sx={{ m: 0, fontSize: 11, color: "#64748b", lineHeight: 1.6 }}>
                  Conditions emitted when this task fails. Same shape as the LOS document conditions —
                  the LOS consumes both indistinguishably.
                </Box>
                <ConditionsEditor
                  conditions={documentConditions}
                  setConditions={(next) => {
                    setDocumentConditions(next);
                    markDirty();
                  }}
                  productSuggestions={["DSCR", "RTL", "GUC", "MF"].filter((k) => productApplicability[k])}
                  purposeSuggestions={[]}
                  emptyLabel="No conditions defined yet — add one to fire on task failure."
                />
              </Box>
            )}

            {tab === "related" && (
              <RelatedTab
                currentTaskId={task.id}
                allTasks={allTasks}
                relatedTaskIds={relatedTaskIds}
                setRelatedTaskIds={(v) => {
                  setRelatedTaskIds(v);
                  markDirty();
                }}
                relatedDocuments={relatedDocuments}
                setRelatedDocuments={(v) => {
                  setRelatedDocuments(v);
                  markDirty();
                }}
              />
            )}

            {tab === "audit" && <AuditTab loading={auditLoading} audit={audit} />}
          </DrawerErrorBoundary>
        </Box>
      </Box>
    </>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function TabButton({ id, icon, label, tab, setTab }) {
  const active = tab === id;
  return (
    <Box
      component="button"
      onClick={() => setTab(id)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        px: "10px",
        py: "6px",
        borderRadius: "4px",
        fontSize: 12,
        cursor: "pointer",
        background: "none",
        ...(active
          ? { bgcolor: "#eff6ff", color: "#1e40af", border: "1px solid #bfdbfe" }
          : {
              color: "#64748b",
              border: "1px solid transparent",
              "&:hover": { color: "#0f172a", bgcolor: "#f3f4f6" },
            }),
      }}
    >
      {icon}
      {label}
    </Box>
  );
}

// RFF-styled field shell — reuses the same primitives as the create-loan
// form's Rff* inputs (InputWrapper for the label/helper, with TextInput /
// SelectInput as children), but driven by the drawer's local state instead of
// react-final-form. Gives the Details pane the same look as the create-loan form.
function RffField({ label, required, helperText, children }) {
  return (
    <InputWrapper fullWidth label={label} required={required} helperText={helperText}>
      {children}
    </InputWrapper>
  );
}

function Section({ label, hint, children }) {
  return (
    <Box component="section" sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      <Box>
        <Box
          component="p"
          sx={{
            m: 0,
            fontSize: 11,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            color: "#64748b",
            fontWeight: 500,
          }}
        >
          {label}
        </Box>
        {hint && (
          <Box component="p" sx={{ m: 0, mt: "2px", fontSize: 11, color: "#64748b" }}>
            {hint}
          </Box>
        )}
      </Box>
      {children}
    </Box>
  );
}

function RelatedTab({
  currentTaskId,
  allTasks,
  relatedTaskIds,
  setRelatedTaskIds,
  relatedDocuments,
  setRelatedDocuments,
}) {
  const [filter, setFilter] = useState("");
  const [docDraft, setDocDraft] = useState("");

  const linkedSet = useMemo(() => new Set(relatedTaskIds), [relatedTaskIds]);
  const taskById = useMemo(() => {
    const m = new Map();
    allTasks.forEach((t) => m.set(t.id, t));
    return m;
  }, [allTasks]);

  const candidates = useMemo(
    () =>
      allTasks
        .filter((t) => t.id !== currentTaskId && !linkedSet.has(t.id))
        .filter((t) => `${t.sourceDocument} ${t.taskText}`.toLowerCase().includes(filter.toLowerCase()))
        .slice(0, 80),
    [allTasks, currentTaskId, linkedSet, filter],
  );

  const addDoc = () => {
    const v = docDraft.trim();
    if (!v || relatedDocuments.includes(v)) return;
    setRelatedDocuments([...relatedDocuments, v]);
    setDocDraft("");
  };

  const inputSx = {
    width: "100%",
    px: "10px",
    py: "6px",
    bgcolor: "#fff",
    border: "1px solid #d1d5db",
    borderRadius: "4px",
    fontSize: 14,
    color: "#0f172a",
    fontFamily: "inherit",
    boxSizing: "border-box",
    "&:focus": { outline: "none", boxShadow: "0 0 0 2px rgba(59,130,246,0.4)" },
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <Section label={`Related tasks (${relatedTaskIds.length})`} hint="Tasks that reference the same docs / risks.">
        {relatedTaskIds.length > 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", mb: "8px" }}>
            {relatedTaskIds.map((id) => {
              const t = taskById.get(id);
              return (
                <Box
                  key={id}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: "8px",
                    px: "10px",
                    py: "6px",
                    bgcolor: "#f9fafb",
                    border: "1px solid #e5e7eb",
                    borderRadius: "4px",
                    fontSize: 12,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Box component="p" sx={{ m: 0, color: "#64748b" }}>
                      {t?.sourceDocument || "—"}
                    </Box>
                    <Box
                      component="p"
                      sx={{
                        m: 0,
                        color: "#0f172a",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t?.taskText || `(missing task ${id.slice(0, 8)})`}
                    </Box>
                  </Box>
                  <Box
                    component="button"
                    onClick={() => setRelatedTaskIds(relatedTaskIds.filter((x) => x !== id))}
                    sx={{
                      color: "#64748b",
                      border: "none",
                      background: "none",
                      p: 0,
                      cursor: "pointer",
                      display: "inline-flex",
                      flexShrink: 0,
                      "&:hover": { color: "#be123c" },
                    }}
                  >
                    <CloseRounded sx={{ fontSize: 11 }} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
        <Box sx={{ position: "relative" }}>
          <SearchOutlined sx={{ fontSize: 12, position: "absolute", left: "10px", top: "10px", color: "#64748b" }} />
          <Box
            component="input"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search tasks…"
            sx={{ ...inputSx, pl: "28px", pr: "12px" }}
          />
        </Box>
        <Box sx={{ maxHeight: 192, overflow: "auto", border: "1px solid #e5e7eb", borderRadius: "4px", mt: "6px" }}>
          {candidates.length === 0 ? (
            <Box component="p" sx={{ m: 0, p: "12px", fontSize: 12, color: "#64748b", fontStyle: "italic" }}>
              No matches.
            </Box>
          ) : (
            candidates.map((t) => (
              <Box
                component="button"
                key={t.id}
                onClick={() => setRelatedTaskIds([...relatedTaskIds, t.id])}
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: "8px",
                  px: "12px",
                  py: "6px",
                  fontSize: 12,
                  textAlign: "left",
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  "&:hover": { bgcolor: "#f3f4f6" },
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Box component="p" sx={{ m: 0, color: "#64748b" }}>
                    {t.sourceDocument || "—"}
                  </Box>
                  <Box
                    component="p"
                    sx={{
                      m: 0,
                      color: "#334155",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t.taskText}
                  </Box>
                </Box>
                <AddRounded sx={{ fontSize: 11, color: "#2563eb", flexShrink: 0, mt: "2px" }} />
              </Box>
            ))
          )}
        </Box>
      </Section>

      <Section label={`Related documents (${relatedDocuments.length})`} hint="LOS docTypes this task touches.">
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
          {relatedDocuments.map((d) => (
            <Box
              component="span"
              key={d}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                px: "8px",
                py: "2px",
                borderRadius: 999,
                fontSize: 11,
                bgcolor: "#ecfdf5",
                color: "#065f46",
                border: "1px solid #a7f3d0",
              }}
            >
              {d}
              <Box
                component="button"
                onClick={() => setRelatedDocuments(relatedDocuments.filter((x) => x !== d))}
                sx={{
                  border: "none",
                  background: "none",
                  p: 0,
                  cursor: "pointer",
                  display: "inline-flex",
                  color: "inherit",
                  "&:hover": { color: "#0f172a" },
                }}
              >
                <CloseRounded sx={{ fontSize: 10 }} />
              </Box>
            </Box>
          ))}
        </Box>
        <Box sx={{ display: "flex", gap: "8px", mt: "8px" }}>
          <Box
            component="input"
            value={docDraft}
            onChange={(e) => setDocDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === ",") {
                e.preventDefault();
                addDoc();
              }
            }}
            placeholder="docType (Enter to add)"
            sx={{
              ...inputSx,
              flex: 1,
              width: "auto",
              "&:focus": { outline: "none", boxShadow: "0 0 0 2px rgba(16,185,129,0.4)" },
            }}
          />
          <Box
            component="button"
            onClick={addDoc}
            sx={{
              px: "10px",
              py: "4px",
              fontSize: 12,
              bgcolor: "#ecfdf5",
              color: "#065f46",
              borderRadius: "4px",
              border: "1px solid #a7f3d0",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              "&:hover": { bgcolor: "#a7f3d0" },
            }}
          >
            <AddRounded sx={{ fontSize: 11 }} />
          </Box>
        </Box>
      </Section>
    </Box>
  );
}

function AuditTab({ loading, audit }) {
  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px", fontSize: 12, color: "#64748b" }}>
        <CircularProgress size={12} /> Loading change history…
      </Box>
    );
  }
  if (!audit.length) {
    return (
      <Box component="p" sx={{ m: 0, fontSize: 12, color: "#64748b", fontStyle: "italic" }}>
        No edits yet.
      </Box>
    );
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {audit.map((entry) => (
        <Box key={entry.id} sx={{ border: "1px solid #e5e7eb", borderRadius: "8px", p: "12px", bgcolor: "#f9fafb" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "8px" }}>
            <Box component="span" sx={{ fontSize: 12, color: "#334155" }}>
              {entry.edited_by}
            </Box>
            <Box component="span" sx={{ fontSize: 11, color: "#64748b" }}>
              {formatDateTime(entry.edited_at, "datetime")}
            </Box>
          </Box>
          <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "4px" }}>
            {entry.changes.map((c, i) => (
              <Box component="li" key={i} sx={{ fontSize: 11 }}>
                <Box component="span" sx={{ color: "#1d4ed8", fontWeight: 500 }}>
                  {c.field}
                </Box>
                <Box component="span" sx={{ color: "#64748b", mx: "4px" }}>
                  ·
                </Box>
                <Box component="span" sx={{ color: "#be123c", textDecoration: "line-through" }}>
                  {compactValue(c.before)}
                </Box>
                <Box component="span" sx={{ color: "#64748b", mx: "4px" }}>
                  →
                </Box>
                <Box component="span" sx={{ color: "#047857" }}>
                  {compactValue(c.after)}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function compactValue(v) {
  if (v === null || v === undefined) return "∅";
  if (typeof v === "string") return v.length > 60 ? v.slice(0, 57) + "…" : v;
  if (typeof v === "boolean" || typeof v === "number") return String(v);
  try {
    const s = JSON.stringify(v);
    return s.length > 80 ? s.slice(0, 77) + "…" : s;
  } catch {
    return String(v);
  }
}
